import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, InputGroup, Badge } from 'react-bootstrap';
import { User } from '../../models/user';

interface ConfirmDialogProps {
  show: boolean;
  type: 'edit' | 'delete';
  user: User | null;
  onConfirm: (user: User) => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  show,
  type,
  user,
  onConfirm,
  onCancel,
}) => {
  const [editedUser, setEditedUser] = useState<User>({
    id: 0,
    username: '',
    password: '',
    email: '',
    isActive: true,
  });
  const [isValid, setIsValid] = useState<boolean>(true);
  const [errors, setErrors] = useState<{
    username?: string;
    email?: string;
    password?: string;
  }>({});

  // ユーザーデータを更新
  useEffect(() => {
    if (user) {
      setEditedUser(user);
      setErrors({});
      setIsValid(true);
    }
  }, [user]);

  // バリデーション
  const validateForm = (): boolean => {
    const newErrors: { username?: string; email?: string; password?: string } =
      {};

    if (!editedUser.username.trim()) {
      newErrors.username = 'ユーザー名を入力してください';
    } else if (editedUser.username.length < 2) {
      newErrors.username = 'ユーザー名は2文字以上で入力してください';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editedUser.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!emailRegex.test(editedUser.email)) {
      newErrors.email = '正しいメールアドレス形式で入力してください';
    }

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    if (type === 'edit' && editedUser.password !== undefined) {
      // 編集モードでパスワードが提供されている場合のみバリデーション
      if (editedUser.password && !passwordRegex.test(editedUser.password)) {
        newErrors.password =
          'パスワードは8文字以上で、英字と数字を含める必要があります';
      }
    }

    setErrors(newErrors);
    const valid = Object.keys(newErrors).length === 0;
    setIsValid(valid);
    return valid;
  };

  const handleConfirm = (): void => {
    if (type === 'edit' && !validateForm()) {
      return;
    }
    onConfirm(type === 'edit' ? editedUser : user!);
  };

  const handleUsernameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newUsername = e.target.value;
    setEditedUser({ ...editedUser, username: newUsername });

    // リアルタイムバリデーション
    if (errors.username && newUsername.trim() && newUsername.length >= 2) {
      setErrors({ ...errors, username: undefined });
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newEmail = e.target.value;
    setEditedUser({ ...editedUser, email: newEmail });

    // リアルタイムバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (errors.email && newEmail.trim() && emailRegex.test(newEmail)) {
      setErrors({ ...errors, email: undefined });
    }
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newPassword = e.target.value;
    setEditedUser({ ...editedUser, password: newPassword });

    // リアルタイムバリデーション
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    if (errors.password && newPassword && passwordRegex.test(newPassword)) {
      setErrors({ ...errors, password: undefined });
    }
  };

  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered
      size="lg"
      className="user-dialog"
      backdrop="static"
      keyboard={false}
      animation={true}
      dialogClassName="modal-dialog-centered"
    >
      <Modal.Header
        closeButton
        className={
          type === 'edit' ? 'bg-primary text-white' : 'bg-danger text-white'
        }
      >
        <Modal.Title>
          <i
            className={`fas ${type === 'edit' ? 'fa-user-edit' : 'fa-user-times'} me-2`}
          ></i>
          {type === 'edit' ? 'ユーザー情報の編集' : 'ユーザーの削除'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {type === 'edit' ? (
          <div className="edit-form">
            <div className="user-profile-header mb-4 p-3 rounded">
              <div className="d-flex align-items-center">
                <div className="user-avatar-large me-3">
                  <i className="fas fa-user-circle fa-3x"></i>
                </div>
                <div>
                  <h6 className="mb-1">ユーザー情報を更新</h6>
                  <small>ID: #{user?.id}</small>
                </div>
              </div>
            </div>

            <Form>
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  <i className="fas fa-user me-2"></i>ユーザー名
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-at"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={editedUser.username || ''}
                    onChange={handleUsernameChange}
                    isInvalid={!!errors.username}
                    placeholder="ユーザー名を入力してください"
                    className={
                      errors.username
                        ? 'is-invalid'
                        : editedUser.username.length >= 2
                          ? 'is-valid'
                          : ''
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.username}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  <i className="fas fa-envelope me-2"></i>メールアドレス
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-envelope"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    value={editedUser.email || ''}
                    onChange={handleEmailChange}
                    isInvalid={!!errors.email}
                    placeholder="example@domain.com"
                    className={
                      errors.email
                        ? 'is-invalid'
                        : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedUser.email)
                          ? 'is-valid'
                          : ''
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  <i className="fas fa-lock me-2"></i>パスワード
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-key"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="password"
                    value={editedUser.password || ''}
                    onChange={handlePasswordChange}
                    isInvalid={!!errors.password}
                    placeholder="新しいパスワードを入力してください"
                    className={
                      errors.password
                        ? 'is-invalid'
                        : editedUser.password &&
                            /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/.test(
                              editedUser.password
                            )
                          ? 'is-valid'
                          : ''
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </InputGroup>
                <Form.Text className="text-muted">
                  パスワードは8文字以上で、英字と数字を含める必要があります。
                  （変更しない場合は空欄のままにしてください）
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  <i className="fas fa-toggle-on me-2"></i>アカウント状態
                </Form.Label>
                <div className="mt-3">
                  <Form.Check
                    type="switch"
                    id="user-status-switch"
                    label={
                      <span className="d-flex align-items-center">
                        <span className="me-2">アクティブ状態</span>
                        <Badge
                          bg={editedUser.isActive ? 'success' : 'secondary'}
                        >
                          <i
                            className={`fas ${editedUser.isActive ? 'fa-check' : 'fa-pause'} me-1`}
                          ></i>
                          {editedUser.isActive ? 'アクティブ' : '停止中'}
                        </Badge>
                      </span>
                    }
                    checked={editedUser.isActive}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEditedUser({
                        ...editedUser,
                        isActive: e.target.checked,
                      })
                    }
                  />
                </div>
              </Form.Group>
            </Form>
          </div>
        ) : (
          <div className="delete-confirmation">
            <div className="text-center mb-4">
              <i className="fas fa-exclamation-triangle fa-4x mb-3"></i>
              <h5>危険な操作：ユーザー削除</h5>
            </div>

            <Alert variant="danger" className="mb-4">
              <Alert.Heading>
                <i className="fas fa-warning me-2"></i>重要な警告
              </Alert.Heading>
              <p className="mb-0">
                この操作は取り消すことができません。削除されたユーザーのデータは完全に失われます。
              </p>
            </Alert>

            <div className="user-info-card p-4 rounded mb-4">
              <div className="d-flex align-items-center">
                <i className="fas fa-user-circle fa-3x me-3"></i>
                <div>
                  <h6 className="mb-2">削除対象ユーザー</h6>
                  <div className="mb-1">
                    <strong>ユーザー名:</strong> {user?.username}
                  </div>
                  <div className="mb-1">
                    <strong>メールアドレス:</strong> {user?.email}
                  </div>
                  <div>
                    <strong>ユーザーID:</strong> #{user?.id}
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="fw-bold text-danger mb-0">
                上記のユーザーを完全に削除しますか？
              </p>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button variant="outline-secondary" onClick={onCancel} size="lg">
          <i className="fas fa-times me-2"></i>キャンセル
        </Button>
        <Button
          variant={type === 'edit' ? 'primary' : 'danger'}
          onClick={handleConfirm}
          size="lg"
          disabled={type === 'edit' && !isValid}
        >
          <i
            className={`fas ${type === 'edit' ? 'fa-save' : 'fa-trash'} me-2`}
          ></i>
          {type === 'edit' ? '変更を保存' : '完全に削除'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDialog;
